import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trackPageview } from './lib/analytics';
import { AuthContext, useAuth } from './lib/auth';
import { copyText, displayUrl, pollShareUrl } from './lib/share';
import { formatCount } from './lib/format';
import { RotatedImageTooLargeError, nextRotation, rotateImageFile } from './lib/rotateImage';
import { Layout } from './components/Chrome';
import PollCard from './components/PollCard';
import { Avatar } from './components/PollView';
import { adminJson, fetchJson, postForm, postJson } from './api';
import AdminShareable from './pages/AdminShareable';
import BoothPage from './pages/Booth';
import CreditsPage from './pages/Credits';
import FeedPage from './pages/Feed';
import Landing from './pages/Landing';
import PollDetailPage from './pages/PollDetail';
import Privacy from './pages/Privacy';
import SharedPollPage from './pages/SharedPoll';
import Tags from './pages/Tags';
import Terms from './pages/Terms';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// Sign in with Apple (web). The Services ID + a return URL registered for the
// votomoto.com web domain must exist in the Apple Developer portal; the button
// stays hidden until REACT_APP_APPLE_SERVICES_ID is set so we never ship a dead
// button. Mirrors the iOS/Android Apple flow — both post to /api/auth/apple.
const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || '';
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== 'undefined' ? `${window.location.origin}/` : '');
const CAPTION_MAX_LENGTH = 280;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const IMAGE_MAX_SIZE = 10 * 1024 * 1024;

// The context lives in lib/auth so pages can read it without importing App;
// re-exported here for the modules that still import it from App.
export { useAuth };

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    async function loadUser() {
      try {
        const data = await fetchJson('/auth/me', { signal: controller.signal });
        setUser(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    }

    loadUser();
    return () => controller.abort();
  }, []);

  async function register(payload) {
    const data = await postJson('/auth/register', payload);
    setUser(data);
    return data;
  }

  async function login(payload) {
    const data = await postJson('/auth/login', payload);
    setUser(data);
    return data;
  }

  async function googleLogin(credential) {
    const data = await postJson('/auth/google', { credential });
    setUser(data);
    return data;
  }

  async function appleLogin(identityToken, name) {
    const data = await postJson('/auth/apple', { identity_token: identityToken, name: name || undefined });
    setUser(data);
    return data;
  }

  async function logout() {
    await postJson('/auth/logout', {});
    // Also drop the separate admin session, so the single header Sign out ends
    // both (no second button needed in the admin panel). No-op if not an admin.
    await adminJson('/admin/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
  }

  // Re-pull /auth/me so an archive claim linked mid-session (admin links while
  // the user is signed in) surfaces without a hard reload (Vector review, #45).
  async function refreshUser() {
    try {
      const data = await fetchJson('/auth/me');
      setUser(data);
      return data;
    } catch (err) {
      return null;
    }
  }

  const value = useMemo(
    () => ({ user, loading, register, login, googleLogin, appleLogin, logout, refreshUser }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function GoogleSignInButton({ onCredential, disabled }) {
  // Google's own rendered button (popup flow). The earlier version was a styled
  // <button> that only called google.accounts.id.prompt() — One Tap — which the
  // browser silently suppresses after a dismissal (g_state cooldown), under
  // FedCM restrictions, or when third-party sign-in is blocked, so clicking it
  // could do nothing at all. renderButton() always opens the Google account
  // chooser on click and hands the credential to the same callback.
  const [ready, setReady] = useState(false);
  const [rendered, setRendered] = useState(false);
  const container = useRef(null);
  // Read by the GIS callback, which is registered once per GIS load (not per
  // render: callers pass a fresh onCredential every render, and re-running
  // initialize + renderButton would tear the button down each time) and must
  // not deliver a credential while a submission is already in flight.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return undefined;
    }

    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    const script = existing || document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setReady(true);
    if (!existing) {
      document.body.appendChild(script);
    } else if (window.google?.accounts?.id) {
      setReady(true);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!ready || !window.google?.accounts?.id || !container.current) {
      return;
    }
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: ({ credential }) => {
        if (credential && !disabledRef.current) {
          onCredentialRef.current(credential);
        }
      },
    });
    container.current.innerHTML = '';
    const width = Math.min(400, Math.max(200, Math.round(container.current.getBoundingClientRect().width || 320)));
    window.google.accounts.id.renderButton(container.current, {
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      text: 'continue_with',
      width,
    });
    setRendered(container.current.childElementCount > 0);
  }, [ready]);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return (
    // `inert` must be a real boolean: React 19 treats '' as false and drops the
    // attribute, leaving Google's iframe focusable while a request is pending.
    <div className={disabled ? 'opacity-60' : ''} inert={!!disabled} aria-disabled={disabled || undefined} data-testid="google-signin-wrapper">
      <div ref={container} className="flex min-h-[44px] w-full justify-center" data-testid="google-signin" />
      {ready && !rendered ? (
        <button
          className="w-full rounded-[14px] bg-white px-4 py-3 text-sm font-semibold text-[#1f1f1f] transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
          type="button"
          disabled={disabled}
          onClick={() => window.google?.accounts?.id?.prompt()}
        >
          Continue with Google
        </button>
      ) : null}
    </div>
  );
}

export function AppleSignInButton({ onCredential, disabled }) {
  const [ready, setReady] = useState(false);
  const APPLE_SDK = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

  useEffect(() => {
    if (!APPLE_SERVICES_ID) {
      return undefined;
    }
    const existing = document.querySelector(`script[src="${APPLE_SDK}"]`);
    const script = existing || document.createElement('script');
    script.src = APPLE_SDK;
    script.async = true;
    script.defer = true;
    script.onload = () => setReady(true);
    if (!existing) {
      document.body.appendChild(script);
    } else if (window.AppleID) {
      setReady(true);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!ready || !window.AppleID) {
      return;
    }
    window.AppleID.auth.init({
      clientId: APPLE_SERVICES_ID,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    });
  }, [ready]);

  async function handleClick() {
    if (!window.AppleID) {
      return;
    }
    try {
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      // Apple returns the display name only on first consent.
      const nm = res?.user?.name;
      const fullName = nm ? [nm.firstName, nm.lastName].filter(Boolean).join(' ') : undefined;
      if (idToken) {
        onCredential(idToken, fullName);
      }
    } catch (_) {
      // User cancelled the popup, or it was blocked — no-op.
    }
  }

  if (!APPLE_SERVICES_ID) {
    return null;
  }

  return (
    <button
      className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-black px-4 py-3 text-sm font-semibold text-white ring-1 ring-voto-line transition hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
      type="button"
      disabled={disabled || !ready}
      onClick={handleClick}
    >
      Continue with Apple
    </button>
  );
}

function AuthPage() {
  const { user, login, register, googleLogin, appleLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', handle: '', name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [navigate, user]);

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'register') {
        await register({
          email: form.email,
          password: form.password,
          handle: form.handle || undefined,
          name: form.name || undefined,
        });
      } else {
        await login({ email: form.email, password: form.password });
      }
      navigate('/');
    } catch (err) {
      setError(mode === 'register' ? 'Unable to create that account.' : 'Invalid email or password.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitGoogle(credential) {
    setSubmitting(true);
    setError('');
    try {
      await googleLogin(credential);
      navigate('/');
    } catch (err) {
      setError('Google sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitApple(identityToken, name) {
    setSubmitting(true);
    setError('');
    try {
      await appleLogin(identityToken, name);
      navigate('/');
    } catch (err) {
      setError('Apple sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass = 'mt-1.5 w-full rounded-xl border border-voto-line bg-voto-surface px-3.5 py-2.5 text-[15px] text-voto-fg outline-none transition focus:border-voto-coral';
  return (
    <main className="mx-auto flex w-full max-w-md flex-col px-4 pb-12 pt-8">
      <h1 className="text-2xl font-semibold tracking-tight text-white">
        {mode === 'register' ? 'Create your votomoto account' : 'Sign in to votomoto'}
      </h1>
      <p className="mt-1 text-sm text-voto-muted">
        {mode === 'register'
          ? 'Ask your group, see who voted, and earn credits in the Voting Booth.'
          : 'Welcome back. Your polls, votes and credits are waiting.'}
      </p>

      <div className="mt-6 flex flex-col gap-3">
        <GoogleSignInButton disabled={submitting} onCredential={submitGoogle} />
        <AppleSignInButton disabled={submitting} onCredential={submitApple} />
      </div>

      <nav aria-label="Account" className="mt-6 flex gap-5 border-b border-voto-line">
        {[['login', 'Sign in'], ['register', 'Register']].map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-current={mode === value ? 'page' : undefined}
            onClick={() => setMode(value)}
            className={`-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition ${
              mode === value ? 'border-voto-coral text-white' : 'border-transparent text-voto-muted hover:text-voto-fg'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && <p className="mt-4 rounded-xl bg-voto-coral/10 p-3 text-sm text-voto-coral">{error}</p>}

      <form className="mt-5 space-y-4" onSubmit={submit}>
        <label className="block">
          <span className="text-sm font-medium text-voto-fg">Email</span>
          <input className={fieldClass} name="email" type="email" autoComplete="email" value={form.email} onChange={updateField} required />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-voto-fg">Password</span>
          <input
            className={fieldClass}
            name="password"
            type="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            value={form.password}
            onChange={updateField}
            minLength={8}
            required
          />
        </label>
        {mode === 'register' && (
          <>
            <label className="block">
              <span className="text-sm font-medium text-voto-fg">Handle</span>
              <input className={fieldClass} name="handle" type="text" value={form.handle} onChange={updateField} placeholder="scott" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-voto-fg">Name</span>
              <input className={fieldClass} name="name" type="text" autoComplete="name" value={form.name} onChange={updateField} placeholder="Scott Paul" />
            </label>
          </>
        )}
        <button
          className="w-full rounded-[14px] bg-voto-coral px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-voto-coralDeep disabled:cursor-not-allowed disabled:opacity-60"
          type="submit"
          disabled={submitting}
        >
          {submitting ? 'Working...' : mode === 'register' ? 'Create account' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}

function AdminMetric({ label, total, today }) {
  return (
    <div className="rounded-lg border border-voto-line bg-voto-surface p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-voto-muted">{label}</p>
      <p className="mt-2 text-2xl font-bold text-voto-fg">{total ?? 0}</p>
      <p className="mt-1 text-sm text-voto-muted">{today ?? 0} today</p>
    </div>
  );
}

function AdminGoogleGate({ onAuthorized }) {
  const [credential, setCredential] = useState('');
  const [message, setMessage] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      return undefined;
    }
    if (window.google?.accounts?.id) {
      setReady(true);
      return undefined;
    }

    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    const script = existing || document.createElement('script');
    const handleLoad = () => setReady(true);
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad);
    if (!existing) {
      document.body.appendChild(script);
    } else if (window.google?.accounts?.id) {
      setReady(true);
    }
    return () => {
      script.removeEventListener('load', handleLoad);
      if (!existing && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !ready || !window.google?.accounts?.id) {
      return;
    }
    const buttonContainer = document.getElementById('google-admin-signin');
    if (!buttonContainer) {
      return;
    }
    buttonContainer.innerHTML = '';
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async ({ credential: googleCredential }) => {
        try {
          const user = await adminJson('/admin/auth/google', {
            method: 'POST',
            body: JSON.stringify({ credential: googleCredential }),
          });
          onAuthorized(user);
        } catch (err) {
          setMessage(err.status === 403 ? 'This Google account is not authorized.' : 'Sign-in failed.');
        }
      },
    });
    window.google.accounts.id.renderButton(buttonContainer, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: 280,
    });
  }, [onAuthorized, ready]);

  async function submitCredential(event) {
    event.preventDefault();
    setMessage('');
    try {
      const user = await adminJson('/admin/auth/google', {
        method: 'POST',
        body: JSON.stringify({ credential }),
      });
      onAuthorized(user);
    } catch (err) {
      setMessage(err.status === 403 ? 'This Google account is not authorized.' : 'Sign-in failed.');
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-4 py-12">
      <h1 className="text-2xl font-bold tracking-tight text-voto-fg">Admin sign-in</h1>
      <p className="mt-2 text-sm text-voto-muted">Use the site owner Google account.</p>
      <div id="google-admin-signin" className="mt-6 min-h-[44px]" />
      {!process.env.REACT_APP_GOOGLE_CLIENT_ID && (
        <form className="mt-6 space-y-3" onSubmit={submitCredential}>
          <label className="block text-sm font-medium text-voto-fg" htmlFor="admin-credential">
            Google credential
          </label>
          <textarea
            id="admin-credential"
            className="min-h-24 w-full rounded-lg border border-voto-line p-3 text-sm outline-none focus:border-voto-coral focus:ring-1 focus:ring-voto-coral/30"
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            required
          />
          <button
            className="w-full rounded-lg bg-voto-coral px-4 py-2 text-sm font-medium text-white transition hover:bg-voto-coralDeep"
            type="submit"
          >
            Sign in
          </button>
        </form>
      )}
      {message && <p className="mt-4 rounded-lg bg-voto-coral/10 p-3 text-sm text-voto-coral">{message}</p>}
    </main>
  );
}

// Multi-select state for the bulk-action toolbars. A Set of ids the moderator
// has ticked; resets whenever the underlying view changes (sort/filter/reload).
function useRowSelection() {
  const [selected, setSelected] = useState(() => new Set());
  const toggle = useCallback((id) => {
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const setMany = useCallback((ids, on) => {
    setSelected((current) => {
      const next = new Set(current);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);
  const clear = useCallback(() => setSelected(new Set()), []);
  return { selected, toggle, setMany, clear };
}

// Sticky action bar shown when ≥1 row is ticked. `actions` = [{label, action, danger}].
function BulkBar({ count, actions, onAction, onClear }) {
  if (!count) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-voto-coral/30 bg-voto-raised px-3 py-2 text-sm">
      <span className="font-medium text-voto-fg">{count} selected</span>
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button
            key={a.action}
            type="button"
            onClick={() => onAction(a.action)}
            className={`rounded-md px-2 py-1 text-xs font-medium ${a.danger ? 'bg-voto-coral/10 text-voto-coral hover:bg-voto-coral/20' : 'border border-voto-line bg-voto-surface text-voto-fg hover:bg-voto-line'}`}
          >
            {a.label}
          </button>
        ))}
      </div>
      <button type="button" onClick={onClear} className="ml-auto text-xs text-voto-muted hover:text-voto-fg">
        Clear
      </button>
    </div>
  );
}

// Report-reason category chips (what reporters flagged the item for).
function ReasonChips({ reasons }) {
  if (!reasons || !reasons.length) return null;
  return (
    <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
      {reasons.map((r) => (
        <span key={r} className="rounded-full bg-voto-coral/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-voto-coral">
          {r}
        </span>
      ))}
    </span>
  );
}

// The triage worklist: everything users have flagged that hasn't been resolved,
// worst-first, so a moderator works a queue instead of scrubbing every table.
// "Dismiss" clears the abuse count (false positive); Hide/Delete take it down.
// Controlled by AdminPage (data + onModerated) so the sub-nav badge stays live.
function AdminQueue({ data, loading, onModerated }) {
  async function act(req) {
    await adminJson(req.path, { method: req.method, body: req.body });
    onModerated?.();
  }

  const patchPoll = (id, payload) => act({ path: `/admin/polls/${id}`, method: 'PATCH', body: JSON.stringify(payload) });
  const deletePoll = (id) => window.confirm('Delete this poll?') && act({ path: `/admin/polls/${id}`, method: 'DELETE' });
  const patchComment = (id, payload) => act({ path: `/admin/comments/${id}`, method: 'PATCH', body: JSON.stringify(payload) });
  const deleteComment = (id) => window.confirm('Delete this comment?') && act({ path: `/admin/comments/${id}`, method: 'DELETE' });

  const counts = data.counts || { polls: 0, comments: 0 };
  const empty = !data.polls.length && !data.comments.length;

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-voto-fg">Needs review</h2>
        <span className="rounded-full bg-voto-coral/10 px-2 py-0.5 text-xs font-semibold text-voto-coral">
          {counts.polls} polls · {counts.comments} comments
        </span>
        <button type="button" onClick={onModerated} className="ml-auto text-xs text-voto-muted hover:text-voto-fg">
          Refresh
        </button>
      </div>
      {loading && empty ? (
        <p className="mt-3 text-sm text-voto-muted">Loading queue…</p>
      ) : empty ? (
        <p className="mt-3 rounded-lg border border-voto-line bg-voto-surface p-4 text-sm text-voto-muted">
          Nothing flagged. The queue is clear. 🎉
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-voto-muted">Flagged polls</h3>
            <div className="mt-2 space-y-2">
              {data.polls.map((poll) => (
                <div key={poll.id} className="rounded-lg border border-voto-line bg-voto-surface p-3">
                  <div className="flex items-start gap-2">
                    {poll.image_url && <img src={poll.image_url} alt="" className="h-10 w-10 flex-shrink-0 rounded object-cover" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-voto-fg">{poll.caption || 'Untitled poll'}</p>
                      <p className="mt-0.5 text-xs text-voto-muted">
                        <Link to={`/poll/${poll.id}`} className="text-voto-coral hover:underline">#{poll.id}</Link>
                        {' · '}<span className="font-semibold text-voto-coral">{poll.inappropriateness ?? 0} flags</span>
                        {' · '}{poll.admin_hidden ? 'hidden' : 'visible'}
                        <ReasonChips reasons={poll.reasons} />
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => patchPoll(poll.id, { admin_hidden: !poll.admin_hidden })}>
                      {poll.admin_hidden ? 'Unhide' : 'Hide'}
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => patchPoll(poll.id, { reset_inappropriateness: true })}>
                      Dismiss
                    </button>
                    <button className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs text-voto-coral hover:bg-voto-coral/20" type="button" onClick={() => deletePoll(poll.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!data.polls.length && <p className="text-xs text-voto-muted">No flagged polls.</p>}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-voto-muted">Flagged comments</h3>
            <div className="mt-2 space-y-2">
              {data.comments.map((comment) => (
                <div key={comment.id} className="rounded-lg border border-voto-line bg-voto-surface p-3">
                  <p className="text-xs font-medium text-voto-muted">{comment.display_name || 'Voto Memories'}</p>
                  <p className="mt-0.5 text-sm text-voto-fg">{comment.body || 'Empty comment'}</p>
                  <p className="mt-1 text-xs text-voto-muted">
                    <Link to={`/poll/${comment.poll_id}`} className="text-voto-coral hover:underline">Poll #{comment.poll_id}</Link>
                    {' · '}<span className="font-semibold text-voto-coral">{comment.inappropriateness ?? 0} flags</span>
                    {' · '}{comment.hidden ? 'hidden' : 'visible'}
                    <ReasonChips reasons={comment.reasons} />
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => patchComment(comment.id, { hidden: !comment.hidden })}>
                      {comment.hidden ? 'Unhide' : 'Hide'}
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => patchComment(comment.id, { reset_inappropriateness: true })}>
                      Dismiss
                    </button>
                    <button className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs text-voto-coral hover:bg-voto-coral/20" type="button" onClick={() => deleteComment(comment.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!data.comments.length && <p className="text-xs text-voto-muted">No flagged comments.</p>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const POLL_PAGE_SIZE = 50;

function AdminPolls({ polls, onModerated }) {
  const [sort, setSort] = useState('recent');
  const [rows, setRows] = useState(polls);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState((polls || []).length >= POLL_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const { selected, toggle, setMany, clear } = useRowSelection();

  // Follow the parent-loaded list only on the initial 'recent' view; once the
  // admin picks a sort, that view is owned locally so actions don't reset it.
  useEffect(() => {
    if (sort === 'recent') {
      setRows(polls);
      setOffset(0);
      setHasMore((polls || []).length >= POLL_PAGE_SIZE);
    }
  }, [polls, sort]);

  const load = useCallback(async (nextSort, nextOffset, append) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort: nextSort, limit: String(POLL_PAGE_SIZE), offset: String(nextOffset) });
      const data = await adminJson(`/admin/polls?${params.toString()}`);
      const list = Array.isArray(data) ? data : [];
      setRows((current) => (append ? [...current, ...list] : list));
      setOffset(nextOffset);
      setHasMore(list.length >= POLL_PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch the current sort (first page) after an action so hiding/featuring a
  // poll keeps you in e.g. the Flagged view, and refresh the queue/metrics.
  async function reload() {
    await load(sort, 0, false);
    onModerated?.();
  }

  async function changeSort(next) {
    setSort(next);
    clear();
    await load(next, 0, false);
  }

  async function updatePoll(id, payload) {
    await adminJson(`/admin/polls/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    await reload();
  }

  async function deletePoll(id) {
    if (!window.confirm('Delete this poll?')) return;
    await adminJson(`/admin/polls/${id}`, { method: 'DELETE' });
    await reload();
  }

  async function bulk(action) {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} poll(s)? This cannot be undone.`)) return;
    await adminJson('/admin/polls/bulk', { method: 'POST', body: JSON.stringify({ ids, action }) });
    clear();
    await reload();
  }

  const pageIds = rows.map((p) => p.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-voto-fg">Poll moderation</h2>
        <label className="flex items-center gap-2 text-sm text-voto-muted">
          Sort
          <select
            value={sort}
            onChange={(e) => changeSort(e.target.value)}
            className="rounded-md border border-voto-line bg-voto-surface px-2 py-1 text-sm"
          >
            <option value="recent">Most recent</option>
            <option value="curated">Curated (iOS feed)</option>
            <option value="abuse">Flagged (abuse)</option>
            <option value="comments">Most comments</option>
          </select>
        </label>
      </div>
      <BulkBar
        count={selected.size}
        actions={[
          { label: 'Hide', action: 'hide' },
          { label: 'Unhide', action: 'unhide' },
          { label: 'Dismiss flags', action: 'reset_abuse' },
          { label: 'Delete', action: 'delete', danger: true },
        ]}
        onAction={bulk}
        onClear={clear}
      />
      <div className="mt-3 overflow-x-auto rounded-lg border border-voto-line bg-voto-surface">
        <table className="min-w-full divide-y divide-voto-line text-sm">
          <thead className="bg-voto-raised text-left text-xs uppercase tracking-wide text-voto-muted">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  checked={allOnPageSelected}
                  onChange={(e) => setMany(pageIds, e.target.checked)}
                />
              </th>
              <th className="px-3 py-2">Poll</th>
              <th className="px-3 py-2">Counts</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-voto-line">
            {rows.map((poll) => (
              <tr key={poll.id} className={selected.has(poll.id) ? 'bg-voto-raised' : undefined}>
                <td className="px-3 py-3 align-top">
                  <input
                    type="checkbox"
                    aria-label={`Select poll ${poll.id}`}
                    checked={selected.has(poll.id)}
                    onChange={() => toggle(poll.id)}
                  />
                </td>
                <td className="max-w-xs px-3 py-3">
                  <div className="flex items-start gap-2">
                    {poll.image_url && (
                      <img src={poll.image_url} alt="" className="h-12 w-12 flex-shrink-0 rounded object-cover" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-voto-fg">{poll.caption || 'Untitled poll'}</p>
                      <Link to={`/poll/${poll.id}`} className="mt-1 inline-block text-xs text-voto-coral hover:underline">#{poll.id}</Link>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-voto-muted">
                  {poll.votes_count ?? 0} votes<br />
                  {poll.comments_count ?? 0} comments
                </td>
                <td className="px-3 py-3 text-voto-muted">
                  abuse {poll.inappropriateness ?? 0}<br />
                  {poll.admin_hidden ? 'hidden' : 'visible'}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={`rounded-md border px-2 py-1 text-xs font-medium ${poll.curated ? 'border-voto-coral bg-voto-coral text-white' : 'border-voto-line bg-voto-raised text-voto-fg hover:bg-voto-line'}`}
                      type="button"
                      onClick={() => updatePoll(poll.id, { curated: !poll.curated })}
                    >
                      {poll.curated ? '✓ In iOS feed' : 'Add to iOS feed'}
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updatePoll(poll.id, { featured: !poll.featured_at })}>
                      {poll.featured_at ? 'Unfeature' : 'Feature'}
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updatePoll(poll.id, { popular: !poll.popular })}>
                      {poll.popular ? 'Unpopular' : 'Popular'}
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updatePoll(poll.id, { admin_hidden: !poll.admin_hidden })}>
                      {poll.admin_hidden ? 'Unhide' : 'Hide'}
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updatePoll(poll.id, { poll_of_the_day: true })}>
                      Poll of day
                    </button>
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updatePoll(poll.id, { reset_inappropriateness: true })}>
                      Reset abuse
                    </button>
                    <button className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs text-voto-coral hover:bg-voto-coral/20" type="button" onClick={() => deletePoll(poll.id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="mt-4">
          <button
            type="button"
            disabled={loading}
            onClick={() => load(sort, offset + POLL_PAGE_SIZE, true)}
            className="rounded-md border border-voto-line bg-voto-raised px-4 py-2 text-sm text-voto-fg hover:bg-voto-line disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

const COMMENT_PAGE_SIZE = 50;

function AdminComments({ comments, onModerated }) {
  const [sort, setSort] = useState('recent');
  const [rows, setRows] = useState(comments);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState((comments || []).length >= COMMENT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const { selected, toggle, setMany, clear } = useRowSelection();

  useEffect(() => {
    if (sort === 'recent') {
      setRows(comments);
      setOffset(0);
      setHasMore((comments || []).length >= COMMENT_PAGE_SIZE);
    }
  }, [comments, sort]);

  const load = useCallback(async (nextSort, nextOffset, append) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort: nextSort, limit: String(COMMENT_PAGE_SIZE), offset: String(nextOffset) });
      const data = await adminJson(`/admin/comments?${params.toString()}`);
      const list = Array.isArray(data) ? data : [];
      setRows((current) => (append ? [...current, ...list] : list));
      setOffset(nextOffset);
      setHasMore(list.length >= COMMENT_PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }, []);

  async function reload() {
    await load(sort, 0, false);
    onModerated?.();
  }

  async function changeSort(next) {
    setSort(next);
    clear();
    await load(next, 0, false);
  }

  async function updateComment(id, payload) {
    await adminJson(`/admin/comments/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    await reload();
  }

  async function deleteComment(id) {
    if (!window.confirm('Delete this comment?')) return;
    await adminJson(`/admin/comments/${id}`, { method: 'DELETE' });
    await reload();
  }

  async function bulk(action) {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} comment(s)? This cannot be undone.`)) return;
    await adminJson('/admin/comments/bulk', { method: 'POST', body: JSON.stringify({ ids, action }) });
    clear();
    await reload();
  }

  const pageIds = rows.map((c) => c.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-voto-fg">Comment moderation</h2>
        <label className="flex items-center gap-2 text-sm text-voto-muted">
          Sort
          <select
            value={sort}
            onChange={(e) => changeSort(e.target.value)}
            className="rounded-md border border-voto-line bg-voto-surface px-2 py-1 text-sm"
          >
            <option value="recent">Most recent</option>
            <option value="flagged">Flagged (abuse)</option>
            <option value="hidden">Hidden</option>
          </select>
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="checkbox"
          aria-label="Select all on page"
          checked={allOnPageSelected}
          onChange={(e) => setMany(pageIds, e.target.checked)}
        />
        <span className="text-xs text-voto-muted">Select all on page</span>
      </div>
      <BulkBar
        count={selected.size}
        actions={[
          { label: 'Hide', action: 'hide' },
          { label: 'Unhide', action: 'unhide' },
          { label: 'Delete', action: 'delete', danger: true },
        ]}
        onAction={bulk}
        onClear={clear}
      />
      <div className="mt-3 space-y-3">
        {rows.map((comment) => (
          <div key={comment.id} className={`rounded-lg border border-voto-line bg-voto-surface p-4 ${selected.has(comment.id) ? 'ring-1 ring-voto-coral/40' : ''}`}>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                aria-label={`Select comment ${comment.id}`}
                checked={selected.has(comment.id)}
                onChange={() => toggle(comment.id)}
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-voto-muted">
                  {comment.author_handle ? (
                    <Link to={`/u/${comment.author_handle}`} className="text-voto-coral hover:underline">@{comment.author_handle}</Link>
                  ) : (
                    comment.display_name || 'Voto Memories'
                  )}
                </p>
                <p className="mt-0.5 text-sm text-voto-fg">{comment.body || 'Empty comment'}</p>
                <p className="mt-2 text-xs text-voto-muted">
                  <Link to={`/poll/${comment.poll_id}`} className="text-voto-coral hover:underline">Poll #{comment.poll_id}</Link>
                  {' · '}abuse {comment.inappropriateness ?? 0} · {comment.hidden ? 'hidden' : 'visible'}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updateComment(comment.id, { hidden: !comment.hidden })}>
                    {comment.hidden ? 'Unhide' : 'Hide'}
                  </button>
                  {(comment.inappropriateness ?? 0) > 0 && (
                    <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => updateComment(comment.id, { reset_inappropriateness: true })}>
                      Dismiss flags
                    </button>
                  )}
                  <button className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs text-voto-coral hover:bg-voto-coral/20" type="button" onClick={() => deleteComment(comment.id)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-voto-muted">{loading ? 'Loading…' : 'No comments.'}</p>
        )}
      </div>
      {hasMore && (
        <div className="mt-4">
          <button
            type="button"
            disabled={loading}
            onClick={() => load(sort, offset + COMMENT_PAGE_SIZE, true)}
            className="rounded-md border border-voto-line bg-voto-raised px-4 py-2 text-sm text-voto-fg hover:bg-voto-line disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

const TAG_PAGE_SIZE = 100;

function AdminTags({ tags }) {
  const [items, setItems] = useState(tags);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState((tags || []).length >= TAG_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [mergeTargets, setMergeTargets] = useState({});

  // While not searching, follow the parent-loaded first page.
  useEffect(() => {
    if (!search) {
      setItems(tags);
      setOffset(0);
      setHasMore((tags || []).length >= TAG_PAGE_SIZE);
    }
  }, [tags, search]);

  const load = useCallback(async (q, nextOffset, append) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(TAG_PAGE_SIZE), offset: String(nextOffset) });
      if (q) {
        params.set('q', q);
      }
      const rows = await adminJson(`/admin/tags?${params.toString()}`);
      const list = Array.isArray(rows) ? rows : [];
      setItems((current) => (append ? [...current, ...list] : list));
      setOffset(nextOffset);
      setHasMore(list.length >= TAG_PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => load(search, 0, false), [load, search]);

  function submitSearch(event) {
    event.preventDefault();
    const next = query.trim();
    setSearch(next);
    load(next, 0, false);
  }

  function clearSearch() {
    setQuery('');
    setSearch('');
    setItems(tags);
    setOffset(0);
    setHasMore((tags || []).length >= TAG_PAGE_SIZE);
  }

  async function renameTag(tag) {
    const name = window.prompt('Rename tag', tag.name);
    if (!name || name === tag.name) {
      return;
    }
    await adminJson(`/admin/tags/${tag.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    refresh();
  }

  async function mergeTag(tag) {
    const target = Number(mergeTargets[tag.id]);
    if (!target) {
      return;
    }
    await adminJson(`/admin/tags/${tag.id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ target_tag_id: target }),
    });
    refresh();
  }

  async function deleteTag(id) {
    if (!window.confirm('Delete this tag?')) {
      return;
    }
    await adminJson(`/admin/tags/${id}`, { method: 'DELETE' });
    refresh();
  }

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-voto-fg">Tags</h2>
        <form onSubmit={submitSearch} className="flex gap-2">
          <input
            className="rounded-md border border-voto-line px-3 py-1.5 text-sm text-voto-fg outline-none focus:border-voto-coral"
            placeholder="Search tags…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button className="rounded-md border border-voto-line bg-voto-raised px-3 py-1.5 text-xs text-voto-fg hover:bg-voto-line" type="submit">
            Search
          </button>
          {search && (
            <button type="button" onClick={clearSearch} className="rounded-md border border-voto-line bg-voto-raised px-3 py-1.5 text-xs text-voto-fg hover:bg-voto-line">
              Clear
            </button>
          )}
        </form>
      </div>
      {search && (
        <p className="mt-1 text-xs text-voto-muted">Showing tags matching “{search}”. Merge targets are limited to the tags loaded below.</p>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        {items.map((tag) => (
          <div key={tag.id} className="rounded-lg border border-voto-line bg-voto-surface p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-voto-fg">{tag.name}</p>
                <p className="mt-1 text-xs text-voto-muted">{tag.polls_count ?? 0} polls</p>
              </div>
              <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => renameTag(tag)}>
                Rename
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              <select
                className="min-w-0 flex-1 rounded-md border border-voto-line px-2 py-1 text-sm text-voto-fg outline-none focus:border-voto-coral"
                value={mergeTargets[tag.id] || ''}
                onChange={(event) => setMergeTargets((current) => ({ ...current, [tag.id]: event.target.value }))}
              >
                <option value="">Merge into…</option>
                {items.filter((other) => other.id !== tag.id).map((other) => (
                  <option key={other.id} value={other.id}>{other.name}</option>
                ))}
              </select>
              <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => mergeTag(tag)}>
                Merge
              </button>
              <button className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs text-voto-coral hover:bg-voto-coral/20" type="button" onClick={() => deleteTag(tag.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && (
        <p className="mt-3 text-sm text-voto-muted">{loading ? 'Loading…' : 'No tags found.'}</p>
      )}
      {hasMore && (
        <div className="mt-4">
          <button
            type="button"
            disabled={loading}
            onClick={() => load(search, offset + TAG_PAGE_SIZE, true)}
            className="rounded-md border border-voto-line bg-voto-raised px-4 py-2 text-sm text-voto-fg hover:bg-voto-line disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

// Account-level enforcement: search live accounts, suspend/lift a repeat
// offender (blocks their posting + commenting until reinstated).
const USER_PAGE_SIZE = 25;

// Short date for the admin tables; '—' when there's no timestamp.
function fmtDate(ts) {
  return ts ? new Date(ts).toLocaleDateString() : '—';
}

function AdminUsers() {
  const [query, setQuery] = useState('');     // search input
  const [applied, setApplied] = useState(''); // search term in effect
  const [sort, setSort] = useState('recent');
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (nextSort, q, nextOffset, append) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort: nextSort, limit: String(USER_PAGE_SIZE), offset: String(nextOffset) });
      if (q) params.set('q', q);
      const res = await adminJson(`/admin/users?${params.toString()}`);
      const list = res.items || [];
      setItems((cur) => (append ? [...cur, ...list] : list));
      setOffset(nextOffset);
      setHasMore(list.length >= USER_PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }, []);

  // Default to a browsable list on mount (no search needed).
  useEffect(() => { load('recent', '', 0, false); }, [load]);

  function submit(e) {
    e.preventDefault();
    const q = query.trim();
    setApplied(q);
    load(sort, q, 0, false);
  }

  function changeSort(next) {
    setSort(next);
    load(next, applied, 0, false);
  }

  async function suspend(u) {
    const reason = window.prompt(`Suspend @${u.handle}? This blocks posting & commenting. Reason (optional):`, '');
    if (reason === null) return; // cancelled
    await adminJson(`/admin/app_users/${u.id}/suspend`, { method: 'POST', body: JSON.stringify({ reason }) });
    await load(sort, applied, 0, false);
  }

  async function unsuspend(u) {
    await adminJson(`/admin/app_users/${u.id}/unsuspend`, { method: 'POST' });
    await load(sort, applied, 0, false);
  }

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-voto-fg">Users</h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-voto-muted">
            Sort
            <select
              value={sort}
              onChange={(e) => changeSort(e.target.value)}
              className="rounded-md border border-voto-line bg-voto-surface px-2 py-1 text-sm"
            >
              <option value="recent">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="active">Last active</option>
              <option value="handle">Handle (A–Z)</option>
              <option value="polls">Most polls</option>
              <option value="suspended">Suspended first</option>
            </select>
          </label>
          <form onSubmit={submit} className="flex gap-2">
            <input
              className="rounded-md border border-voto-line px-3 py-1.5 text-sm text-voto-fg outline-none focus:border-voto-coral"
              placeholder="Search handle, name, or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="rounded-md border border-voto-line bg-voto-raised px-3 py-1.5 text-xs text-voto-fg hover:bg-voto-line" type="submit">
              Search
            </button>
            {applied && (
              <button
                type="button"
                onClick={() => { setQuery(''); setApplied(''); load(sort, '', 0, false); }}
                className="rounded-md border border-voto-line bg-voto-raised px-3 py-1.5 text-xs text-voto-fg hover:bg-voto-line"
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>
      {applied && (
        <p className="mt-1 text-xs text-voto-muted">Showing accounts matching “{applied}”.</p>
      )}
      <div className="mt-3 space-y-2">
        {items.map((u) => (
          <div key={`${u.source}-${u.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-voto-line bg-voto-surface p-3">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-2 text-sm font-medium text-voto-fg">
                {u.handle ? (
                  <Link to={`/u/${u.handle}`} className="text-voto-coral hover:underline">@{u.handle}</Link>
                ) : '—'}
                {u.display_name && u.display_name !== u.handle && (
                  <span className="text-xs font-normal text-voto-muted">{u.display_name}</span>
                )}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${u.source === 'app' ? 'bg-voto-raised text-voto-fg' : 'bg-voto-raised text-voto-muted'}`}>
                  {u.source === 'app' ? 'New' : 'Archive'}
                </span>
                {u.suspended_at && (
                  <span className="rounded-full bg-voto-coral/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-voto-coral">
                    Suspended
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-xs text-voto-muted">
                {u.email ? `${u.email} · ` : ''}{u.polls_count ?? 0} polls · joined {fmtDate(u.created_at)} · last active {fmtDate(u.last_active_at)}
                {u.suspended_reason ? ` · reason: ${u.suspended_reason}` : ''}
              </p>
            </div>
            {u.source === 'app' && (
              u.suspended_at ? (
                <button className="rounded-md border border-voto-line bg-voto-raised px-2 py-1 text-xs text-voto-fg hover:bg-voto-line" type="button" onClick={() => unsuspend(u)}>
                  Lift suspension
                </button>
              ) : (
                <button className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs font-medium text-voto-coral hover:bg-voto-coral/20" type="button" onClick={() => suspend(u)}>
                  Suspend
                </button>
              )
            )}
          </div>
        ))}
        {!items.length && (
          <p className="text-sm text-voto-muted">{loading ? 'Loading…' : 'No accounts found.'}</p>
        )}
      </div>
      {hasMore && (
        <div className="mt-4">
          <button
            type="button"
            disabled={loading}
            onClick={() => load(sort, applied, offset + USER_PAGE_SIZE, true)}
            className="rounded-md border border-voto-line bg-voto-raised px-4 py-2 text-sm text-voto-fg hover:bg-voto-line disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

// Read-only audit trail: who moderated what, newest first.
function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const AUDIT_PAGE = 50;

  const load = useCallback(async (nextOffset, append) => {
    setLoading(true);
    try {
      const data = await adminJson(`/admin/audit?limit=${AUDIT_PAGE}&offset=${nextOffset}`);
      const list = Array.isArray(data) ? data : [];
      setRows((cur) => (append ? [...cur, ...list] : list));
      setOffset(nextOffset);
      setHasMore(list.length >= AUDIT_PAGE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, false); }, [load]);

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-voto-fg">Audit log</h2>
      <div className="mt-3 overflow-x-auto rounded-lg border border-voto-line bg-voto-surface">
        <table className="min-w-full divide-y divide-voto-line text-sm">
          <thead className="bg-voto-raised text-left text-xs uppercase tracking-wide text-voto-muted">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Moderator</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-voto-line">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="whitespace-nowrap px-3 py-2 text-voto-muted">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-voto-fg">{r.admin_email}</td>
                <td className="px-3 py-2 font-medium text-voto-fg">{r.action}{r.detail ? ` (${r.detail})` : ''}</td>
                <td className="px-3 py-2 text-voto-muted">
                  {r.target_id ? `${r.target_type} #${r.target_id}` : r.target_type}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.length && <p className="mt-3 text-sm text-voto-muted">{loading ? 'Loading…' : 'No actions logged yet.'}</p>}
      {hasMore && (
        <div className="mt-4">
          <button type="button" disabled={loading} onClick={() => load(offset + AUDIT_PAGE, true)} className="rounded-md border border-voto-line bg-voto-raised px-4 py-2 text-sm text-voto-fg hover:bg-voto-line disabled:opacity-50">
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

// Manage who can access moderation. Owners come from the deploy-config allowlist
// (un-removable here); everyone else is granted/revoked from this tab. Built so
// human moderators can be added as content moderation scales.
function AdminAdmins({ currentEmail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminJson('/admin/admins');
      setItems(res.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function add(e) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setBusy(true);
    setError('');
    try {
      await adminJson('/admin/admins', { method: 'POST', body: JSON.stringify({ query: q }) });
      setQuery('');
      await load();
    } catch (err) {
      setError(err.detail || 'Could not add that admin.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(a) {
    if (!window.confirm(`Remove admin access for ${a.email || '@' + a.handle}?`)) return;
    setBusy(true);
    setError('');
    try {
      await adminJson(`/admin/admins/${a.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err.detail || 'Could not remove that admin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-voto-fg">Admins</h2>
          <p className="mt-0.5 text-xs text-voto-muted">People who can access moderation. Owners are set in deploy config and can’t be removed here.</p>
        </div>
        <form onSubmit={add} className="flex gap-2">
          <input
            className="rounded-md border border-voto-line px-3 py-1.5 text-sm text-voto-fg outline-none focus:border-voto-coral"
            placeholder="Add by email or @handle…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button disabled={busy} className="rounded-md bg-voto-coral px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50" type="submit">
            Add admin
          </button>
        </form>
      </div>
      {error && <p className="mt-3 rounded-lg bg-voto-coral/10 p-3 text-sm text-voto-coral">{error}</p>}
      <div className="mt-3 space-y-2">
        {items.map((a) => (
          <div key={a.id ? `app-${a.id}` : `env-${a.email}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-voto-line bg-voto-surface p-3">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-x-2 text-sm font-medium text-voto-fg">
                {a.handle ? (
                  <Link to={`/u/${a.handle}`} className="text-voto-coral hover:underline">@{a.handle}</Link>
                ) : (a.display_name || '—')}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${a.source === 'owner' ? 'bg-amber-50 text-amber-700' : 'bg-voto-raised text-voto-fg'}`}>
                  {a.source === 'owner' ? 'Owner' : 'Admin'}
                </span>
                {a.email && currentEmail && a.email.toLowerCase() === currentEmail.toLowerCase() && (
                  <span className="text-[10px] font-normal text-voto-muted">(you)</span>
                )}
              </p>
              <p className="mt-0.5 text-xs text-voto-muted">
                {a.email || 'no email on file'}
                {a.last_active_at ? ` · last active ${fmtDate(a.last_active_at)}` : ''}
              </p>
            </div>
            {a.removable ? (
              <button disabled={busy} className="rounded-md bg-voto-coral/10 px-2 py-1 text-xs font-medium text-voto-coral hover:bg-voto-coral/20 disabled:opacity-50" type="button" onClick={() => remove(a)}>
                Remove
              </button>
            ) : (
              <span className="text-xs text-voto-muted">Set in config</span>
            )}
          </div>
        ))}
        {!items.length && <p className="text-sm text-voto-muted">{loading ? 'Loading…' : 'No admins.'}</p>}
      </div>
    </section>
  );
}

function AdminPage() {
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [polls, setPolls] = useState([]);
  const [comments, setComments] = useState([]);
  const [tags, setTags] = useState([]);
  const [queue, setQueue] = useState({ polls: [], comments: [], counts: { polls: 0, comments: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Active sub-nav pane — one section at a time instead of one long scroll.
  const [tab, setTab] = useState('queue');

  const loadAdmin = useCallback(async () => {
    setError('');
    const [dashboardData, queueData, pollsData, commentsData, tagsData] = await Promise.all([
      adminJson('/admin/dashboard'),
      adminJson('/admin/queue'),
      adminJson('/admin/polls'),
      adminJson('/admin/comments'),
      adminJson('/admin/tags?limit=100'),
    ]);
    setDashboard(dashboardData);
    setQueue({
      polls: queueData?.polls || [],
      comments: queueData?.comments || [],
      counts: queueData?.counts || { polls: 0, comments: 0 },
    });
    setPolls(Array.isArray(pollsData) ? pollsData : []);
    setComments(Array.isArray(commentsData) ? commentsData : []);
    setTags(Array.isArray(tagsData) ? tagsData : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function checkSession() {
      setLoading(true);
      try {
        const session = await adminJson('/admin/session');
        if (!cancelled) {
          setUser(session);
          await loadAdmin();
        }
      } catch (err) {
        if (!cancelled && err.status !== 403) {
          setError('Unable to load admin.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    checkSession();
    return () => {
      cancelled = true;
    };
  }, [loadAdmin]);

  const onAuthorized = useCallback(async (session) => {
    setUser(session);
    setLoading(true);
    try {
      await loadAdmin();
    } finally {
      setLoading(false);
    }
  }, [loadAdmin]);

  if (loading) {
    return <main className="mx-auto w-full max-w-6xl px-4 py-8 text-voto-muted">Loading admin…</main>;
  }

  if (!user) {
    return <AdminGoogleGate onAuthorized={onAuthorized} />;
  }

  const stats = dashboard?.stats || {};

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-voto-fg">Admin</h1>
          <p className="mt-1 text-sm text-voto-muted">{user.email}</p>
        </div>
      </div>
      {error && <p className="mt-6 rounded-lg bg-voto-coral/10 p-3 text-voto-coral">{error}</p>}
      {/* At-a-glance health stays visible across every tab. */}
      <section className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <AdminMetric label="Users" total={stats.users_total} today={stats.users_today} />
        <AdminMetric label="Polls" total={stats.polls_total} today={stats.polls_today} />
        <AdminMetric label="Votes" total={stats.votes_total} today={stats.votes_today} />
        <AdminMetric label="Comments" total={stats.comments_total} today={stats.comments_today} />
      </section>

      <AdminTabs tab={tab} onChange={setTab} queueCount={queue.counts.polls + queue.counts.comments} />

      {tab === 'queue' && <AdminQueue data={queue} loading={loading} onModerated={loadAdmin} />}
      {tab === 'polls' && <AdminPolls polls={polls} onModerated={loadAdmin} />}
      {tab === 'comments' && <AdminComments comments={comments} onModerated={loadAdmin} />}
      {tab === 'users' && <AdminUsers />}
      {tab === 'admins' && <AdminAdmins currentEmail={user.email} />}
      {tab === 'tags' && <AdminTags tags={tags} />}
      {tab === 'shareable' && <AdminShareable />}
      {tab === 'audit' && <AdminAudit />}
    </main>
  );
}

// One-section-at-a-time sub-nav for the admin panel (focus over a long scroll).
// The Queue tab carries a live badge of how many items need review.
function AdminTabs({ tab, onChange, queueCount }) {
  const tabs = [
    ['queue', 'Needs review'],
    ['polls', 'Polls'],
    ['comments', 'Comments'],
    ['users', 'Users'],
    ['admins', 'Admins'],
    ['tags', 'Tags'],
    ['shareable', 'Shareable'],
    ['audit', 'Audit log'],
  ];
  return (
    <nav className="mt-8 flex flex-wrap gap-1 border-b border-voto-line" aria-label="Admin sections">
      {tabs.map(([value, label]) => {
        const active = tab === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => onChange(value)}
            aria-current={active ? 'page' : undefined}
            className={`relative -mb-px rounded-t-lg border-b-2 px-3 py-2 text-sm font-medium transition ${
              active
                ? 'border-voto-coral text-voto-coral'
                : 'border-transparent text-voto-muted hover:text-voto-fg'
            }`}
          >
            {label}
            {value === 'queue' && queueCount > 0 && (
              <span className="ml-1.5 rounded-full bg-voto-coral/20 px-1.5 py-0.5 text-[11px] font-semibold text-voto-coral">
                {queueCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

function HomePage() {
  const { user, loading, logout } = useAuth();
  if (!loading && !user) {
    return <Landing />;
  }
  return (
    <Layout user={user} loading={loading} onLogout={logout}>
      {loading
        ? <main className="mx-auto w-full max-w-xl px-4 py-8 text-voto-muted">Loading…</main>
        : <FeedPage />
      }
    </Layout>
  );
}

function ProfilePage() {
  const { handle } = useParams();
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [credits, setCredits] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // Own-profile sub-tabs: your polls vs. the polls you've voted on (private).
  const [tab, setTab] = useState('polls'); // 'polls' | 'voted'
  const [voted, setVoted] = useState(null);
  const [votedLoading, setVotedLoading] = useState(false);

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type)) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const updated = await postForm('/me/avatar', fd);
      setProfile((p) => ({ ...p, avatar_url: updated.avatar_url }));
      refreshUser();
    } catch (_) {
      // best-effort; leave the existing avatar in place on failure
    } finally {
      setUploadingAvatar(false);
    }
  }
  // Followers/following list panel (#3): which list is open + its members.
  const [listMode, setListMode] = useState(null); // 'followers' | 'following' | null
  const [listItems, setListItems] = useState(null);

  async function openList(mode) {
    if (listMode === mode) { setListMode(null); return; }
    setListMode(mode);
    setListItems(null);
    try {
      setListItems(await fetchJson(`/users/${handle}/${mode}`));
    } catch (_) {
      setListItems([]);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setTab('polls');
    setVoted(null);
    fetchJson(`/users/${handle}`)
      .then((data) => {
        if (!cancelled) {
          setProfile(data);
          setIsFollowing(data.is_following ?? false);
          setFollowerCount(data.follower_count ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [handle]);

  // Your own profile is the You tab: show the credits balance row.
  const ownHandle = user?.handle;
  useEffect(() => {
    if (!ownHandle || ownHandle !== handle) return undefined;
    let cancelled = false;
    fetchJson('/me/credits')
      .then((d) => { if (!cancelled && Number.isFinite(d?.credits)) setCredits(d.credits); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ownHandle, handle]);

  // Lazy-load the "Voted" history the first time that tab is opened.
  useEffect(() => {
    if (tab !== 'voted' || voted !== null || votedLoading) return;
    let cancelled = false;
    setVotedLoading(true);
    fetchJson('/me/voted')
      .then((d) => { if (!cancelled) setVoted(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setVoted([]); })
      .finally(() => { if (!cancelled) setVotedLoading(false); });
    return () => { cancelled = true; };
  }, [tab, voted, votedLoading]);

  async function handleFollowToggle() {
    if (!user || followLoading) return;
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowerCount((c) => wasFollowing ? c - 1 : c + 1);
    setFollowLoading(true);
    try {
      const data = await fetchJson(`/users/${handle}/follow`, {
        method: wasFollowing ? 'DELETE' : 'POST',
      });
      setIsFollowing(data.is_following);
      setFollowerCount(data.follower_count);
    } catch {
      setIsFollowing(wasFollowing);
      setFollowerCount((c) => wasFollowing ? c + 1 : c - 1);
    } finally {
      setFollowLoading(false);
    }
  }

  if (loading) {
    return <main className="mx-auto w-full max-w-xl px-4 py-8 text-voto-muted">Loading…</main>;
  }

  if (notFound || !profile) {
    return (
      <main className="mx-auto w-full max-w-xl px-4 py-8">
        <p className="text-voto-muted">User not found.</p>
      </main>
    );
  }

  const isOwnProfile = user && user.handle === handle;
  const showFollowButton = user && !isOwnProfile;
  const avatar = profile.avatar_url ? (
    <img src={profile.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover" />
  ) : (
    <Avatar name={profile.name || profile.handle} handle={profile.handle} size="h-16 w-16 text-xl" />
  );
  const sections = [['polls', 'Polls'], ['voted', 'Voted']];
  const cards = (list) => (
    <div className="grid gap-1.5 sm:grid-cols-2 sm:gap-4 sm:px-4">
      {list.map((poll) => (
        <PollCard key={poll.id} poll={poll} to={`/poll/${poll.id}`} source="profile" />
      ))}
    </div>
  );

  return (
    <main className="mx-auto w-full max-w-3xl pb-8">
      {/* The header continues the chrome bar (frame B), like the feed's pill row. */}
      <section className="bg-voto-chrome px-4 pb-1 pt-2">
        <div className="flex items-center gap-3.5">
          {isOwnProfile ? (
            <label className="group relative h-16 w-16 flex-shrink-0 cursor-pointer rounded-full focus-within:ring-2 focus-within:ring-voto-coral" title="Change photo">
              {avatar}
              <span className="absolute inset-0 hidden items-center justify-center rounded-full bg-black/55 text-xs font-semibold text-white group-hover:flex">
                {uploadingAvatar ? '…' : 'Change'}
              </span>
              <input
                type="file"
                aria-label="Change profile photo"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                disabled={uploadingAvatar}
                onChange={handleAvatarChange}
              />
            </label>
          ) : (
            <div className="h-16 w-16 flex-shrink-0">{avatar}</div>
          )}
          <div className="min-w-0 flex-1">
            {profile.name && <h1 className="truncate text-lg font-semibold text-white">{profile.name}</h1>}
            <p className="truncate text-sm text-voto-muted">@{profile.handle}</p>
          </div>
          {showFollowButton && (
            <button
              type="button"
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`flex-shrink-0 rounded-[10px] px-4 py-1.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                isFollowing
                  ? 'bg-voto-surface text-voto-fg ring-1 ring-voto-line hover:text-voto-coral'
                  : 'bg-voto-coral text-white hover:bg-voto-coralDeep'
              }`}
            >
              {followLoading ? '…' : isFollowing ? 'Following' : 'Follow'}
            </button>
          )}
        </div>
        {profile.bio && <p className="mt-3 text-sm text-voto-fg">{profile.bio}</p>}
        <div className="mt-3 flex gap-4 text-sm text-voto-muted">
          <button type="button" className="rounded hover:text-white" onClick={() => openList('followers')} aria-expanded={listMode === 'followers'}>
            <strong className="font-semibold text-white">{formatCount(followerCount)}</strong> followers
          </button>
          <button type="button" className="rounded hover:text-white" onClick={() => openList('following')} aria-expanded={listMode === 'following'}>
            <strong className="font-semibold text-white">{formatCount(profile.following_count)}</strong> following
          </button>
        </div>
        {(profile.archive_follower_count > 0 || profile.archive_following_count > 0) && (
          <p className="mt-1 text-xs text-voto-muted">
            {profile.archive_follower_count?.toLocaleString()} followers · {profile.archive_following_count?.toLocaleString()} following from old Voto
          </p>
        )}
        {listMode && (
          <div className="mt-3 rounded-xl bg-voto-bg p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-voto-muted">{listMode}</p>
            {listItems === null ? (
              <p className="text-xs text-voto-muted">Loading…</p>
            ) : listItems.length === 0 ? (
              <p className="text-xs text-voto-muted">No {listMode} yet.</p>
            ) : (
              <ul className="space-y-2">
                {listItems.map((u) => (
                  <li key={u.handle}>
                    <Link to={`/u/${u.handle}`} className="flex items-center gap-2 rounded hover:bg-voto-surface">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <Avatar name={u.name || u.handle} handle={u.handle} size="h-7 w-7 text-[11px]" />
                      )}
                      <span className="text-sm text-voto-fg">{u.name || u.handle}</span>
                      <span className="text-xs text-voto-muted">@{u.handle}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {isOwnProfile ? (
          <nav aria-label="Profile sections" className="mt-3 flex gap-4">
            {sections.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                aria-current={tab === value ? 'page' : undefined}
                className={`border-b-2 px-1 pb-1.5 pt-1 text-[13px] font-medium transition ${
                  tab === value ? 'border-voto-coral text-white' : 'border-transparent text-voto-muted hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        ) : (
          <p className="mt-3 border-b-2 border-voto-coral pb-1.5 pt-1 text-[13px] font-medium text-white" style={{ width: 'fit-content' }}>
            Polls
          </p>
        )}
      </section>

      {isOwnProfile && (
        <div className="flex flex-col gap-2 px-4 py-3">
          <Link
            to="/credits"
            data-testid="credits-row"
            className="flex items-center gap-3 rounded-xl bg-voto-surface px-4 py-3 ring-1 ring-voto-line transition hover:bg-voto-raised"
          >
            <span className="flex-1 text-sm font-semibold text-voto-fg">Credits</span>
            <span className="text-sm text-voto-muted">{credits === null ? 'Balance and history' : formatCount(credits)}</span>
            <span aria-hidden="true" className="text-lg leading-none text-voto-muted">›</span>
          </Link>
          <button
            type="button"
            onClick={async () => { await logout(); navigate('/'); }}
            className="self-start rounded-md px-1 text-sm text-voto-muted transition hover:text-voto-fg sm:hidden"
          >
            Sign out
          </button>
        </div>
      )}

      <section className={isOwnProfile ? '' : 'pt-1.5'}>
        {!isOwnProfile || tab === 'polls' ? (
          profile.polls.length === 0 ? (
            <p className="px-4 py-6 text-voto-muted">No polls yet.</p>
          ) : (
            cards(profile.polls)
          )
        ) : voted === null || votedLoading ? (
          <p className="px-4 py-6 text-voto-muted">Loading…</p>
        ) : voted.length === 0 ? (
          <p className="px-4 py-6 text-voto-muted">You haven't voted on any polls yet.</p>
        ) : (
          cards(voted)
        )}
      </section>
    </main>
  );
}

function emptyOption() {
  return { label: '', file: null, preview: '', error: '', rotation: 0 };
}

function CreatePollPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [caption, setCaption] = useState('');
  const [options, setOptions] = useState(() => [emptyOption(), emptyOption()]);
  const [visibility, setVisibility] = useState('public'); // public | link
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [loading, user, navigate]);

  // Release every object-URL preview when the page unmounts.
  useEffect(() => {
    return () => {
      setOptions((current) => {
        current.forEach((o) => o.preview && URL.revokeObjectURL(o.preview));
        return current;
      });
    };
  }, []);

  function updateLabel(index, value) {
    setOptions((current) => current.map((o, i) => (i === index ? { ...o, label: value } : o)));
  }

  function addOption() {
    setOptions((current) => (current.length < 4 ? [...current, emptyOption()] : current));
  }

  function removeOption(index) {
    setOptions((current) => {
      const target = current[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }

  function handleImageChange(index, event) {
    const file = event.target.files?.[0];
    // Allow re-picking the same file later by clearing the input value.
    event.target.value = '';
    if (!file) return;
    let optionError = '';
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      optionError = 'Image must be JPEG, PNG, or WebP.';
    } else if (file.size > IMAGE_MAX_SIZE) {
      optionError = 'Image must be 10 MB or smaller.';
    }
    setOptions((current) =>
      current.map((o, i) => {
        if (i !== index) return o;
        if (o.preview) URL.revokeObjectURL(o.preview);
        if (optionError) {
          return { ...o, file: null, preview: '', error: optionError, rotation: 0 };
        }
        // A fresh pick starts upright; the rotation belongs to the previous photo.
        return { ...o, file, preview: URL.createObjectURL(file), error: '', rotation: 0 };
      })
    );
  }

  // One tap = 90° clockwise from what the user sees (fracto #533). The preview
  // rotates via CSS; the pixels are rotated on submit (rotateImageFile).
  function rotateOption(index) {
    setOptions((current) =>
      current.map((o, i) => (i === index ? { ...o, rotation: nextRotation(o.rotation) } : o))
    );
  }

  const isValid =
    caption.trim().length > 0 &&
    options.length >= 2 &&
    options.length <= 4 &&
    options.every((o) => o.label.trim() && o.file);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    setError('');
    // Rotated options upload re-encoded pixels (no EXIF, so the server cannot
    // double-rotate); unrotated ones upload the original file untouched.
    // Bounded to the same 10 MB the picker and the API enforce, so an accepted
    // photo cannot become a rejected upload by being rotated.
    const rotated = await Promise.allSettled(
      options.map((o) => rotateImageFile(o.file, o.rotation, { maxBytes: IMAGE_MAX_SIZE }))
    );
    const failed = rotated.findIndex((r) => r.status === 'rejected');
    if (failed !== -1) {
      const n = failed + 1;
      setError(
        rotated[failed].reason instanceof RotatedImageTooLargeError
          ? `Option ${n} photo is still over 10 MB after rotation. Pick a smaller photo.`
          : `Unable to rotate option ${n} photo. Try again or pick it again.`
      );
      setSubmitting(false);
      return;
    }
    const files = rotated.map((r) => r.value);
    const formData = new FormData();
    formData.append('caption', caption.trim());
    formData.append('visibility', visibility);
    options.forEach((o, i) => {
      formData.append('items', o.label.trim());
      formData.append('images', files[i]);
    });
    try {
      const data = await postForm('/polls', formData);
      // Show the share link first (spec §4): the poll is made to be sent.
      setCreated({ ...data, caption: data.caption || caption.trim(), visibility: data.visibility || visibility });
    } catch (err) {
      if (err.status === 401) {
        navigate('/auth');
        return;
      }
      setError(err.detail || 'Unable to create poll right now.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <main className="mx-auto w-full max-w-xl px-4 py-8 text-voto-muted">Loading…</main>;
  }

  if (!user) {
    return null;
  }

  if (created) {
    const url = pollShareUrl(created);
    return (
      <main className="mx-auto w-full max-w-xl px-4 py-8">
        <p className="text-xs font-semibold text-voto-coral">Your poll is live</p>
        <h1 className="mt-1 text-2xl font-semibold text-voto-fg">{created.caption}</h1>
        <p className="mt-2 text-sm text-voto-muted">
          {created.visibility === 'link'
            ? 'Only people with this link can vote. Send it to your group.'
            : 'It’s in the feed and the Voting Booth. Send the link to your group too.'}
        </p>
        <div className="mt-5 rounded-2xl bg-voto-surface p-4 ring-1 ring-voto-line">
          <label htmlFor="share-link" className="text-xs font-medium text-voto-muted">Share link</label>
          <div className="mt-1.5 flex gap-2">
            <input
              id="share-link"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 rounded-lg border border-voto-line bg-voto-bg px-3 py-2 text-sm text-voto-fg outline-none focus:border-voto-coral"
            />
            <button
              type="button"
              onClick={async () => {
                try { if (await copyText(url)) setCopied(true); } catch (_) {}
              }}
              className="flex-none rounded-lg bg-voto-coral px-4 py-2 text-sm font-semibold text-white transition hover:bg-voto-coralDeep"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {copied && <p role="status" className="mt-2 text-xs text-voto-green">Link copied · {displayUrl(url)}</p>}
        </div>
        <div className="mt-5 flex gap-3">
          <Link
            to={`/poll/${created.id}`}
            className="rounded-[12px] bg-voto-surface px-4 py-2.5 text-sm font-semibold text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised"
          >
            View your poll
          </Link>
          <Link to="/feed" className="rounded-[12px] px-4 py-2.5 text-sm font-medium text-voto-muted transition hover:text-voto-fg">
            Back to the feed
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-xl px-4 py-8">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-voto-coral">Ask</p>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight text-white">Create a poll</h1>
      {error && <p className="mt-4 rounded-xl bg-voto-coral/10 p-3 text-sm text-voto-coral">{error}</p>}
      <form className="mt-5 space-y-6" onSubmit={handleSubmit}>
        <label className="block">
          <span className="sr-only">Caption</span>
          <input
            className="w-full rounded-xl border border-voto-line bg-voto-surface px-4 py-3 text-lg font-semibold text-white outline-none transition placeholder:font-normal placeholder:text-voto-muted focus:border-voto-coral"
            maxLength={CAPTION_MAX_LENGTH}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="What's this poll about?"
            type="text"
            value={caption}
          />
        </label>

        <fieldset>
          <legend className="text-sm font-medium text-voto-fg">Options · 2 to 4 photos</legend>
          {/* The options as the tiles they'll become: photo edge to edge, label under. */}
          <div className="mt-2 grid grid-cols-2 gap-[3px] overflow-hidden rounded-2xl">
            {options.map((option, index) => (
              <div key={index} className="flex flex-col bg-voto-surface">
                <div className="relative">
                  <label
                    className="group relative block aspect-square cursor-pointer overflow-hidden bg-voto-raised transition focus-within:ring-[3px] focus-within:ring-inset focus-within:ring-voto-coral"
                    title={option.preview ? 'Change photo' : 'Add photo'}
                  >
                    {option.preview ? (
                      <>
                        {/* The preview is the EXIF-corrected image (from-image), rotated by
                            the user's choice; a square tile stays edge to edge at any step. */}
                        <img
                          src={option.preview}
                          alt=""
                          data-rotation={option.rotation}
                          className="h-full w-full object-cover transition-transform duration-200"
                          style={{ imageOrientation: 'from-image', transform: `rotate(${option.rotation}deg)` }}
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-xs font-semibold text-white opacity-0 transition group-hover:opacity-100">
                          Change photo
                        </span>
                      </>
                    ) : (
                      <span className="flex h-full flex-col items-center justify-center gap-1.5 text-voto-muted transition group-hover:text-white">
                        <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <span className="text-xs font-medium">Add photo</span>
                      </span>
                    )}
                    <input
                      accept="image/jpeg,image/png,image/webp"
                      aria-label={`Option ${index + 1} image`}
                      className="sr-only"
                      onChange={(e) => handleImageChange(index, e)}
                      type="file"
                    />
                  </label>
                  {option.preview && (
                    <button
                      aria-label={`Rotate option ${index + 1} photo`}
                      title="Rotate 90° clockwise"
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-voto-chrome/90 text-white ring-1 ring-voto-line backdrop-blur transition hover:bg-voto-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-voto-coral"
                      onClick={() => rotateOption(index)}
                      type="button"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 12a8 8 0 1 1-2.34-5.66" />
                        <path d="M20 4v5h-5" />
                      </svg>
                      {option.rotation !== 0 && <span className="sr-only">Rotated {option.rotation}°</span>}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <input
                    aria-label={`Option ${index + 1}`}
                    className="min-w-0 flex-1 rounded-md bg-transparent px-1.5 py-1 text-sm font-semibold text-white outline-none placeholder:font-normal placeholder:text-voto-muted focus-visible:ring-1 focus-visible:ring-voto-coral"
                    onChange={(e) => updateLabel(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    type="text"
                    value={option.label}
                  />
                  {options.length > 2 && (
                    <button
                      aria-label={`Remove option ${index + 1}`}
                      className="flex-none rounded-md px-1.5 py-0.5 text-lg leading-none text-voto-muted transition hover:text-voto-coral"
                      onClick={() => removeOption(index)}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </div>
                {option.error && <p className="px-3 pb-2 text-xs text-voto-coral">{option.error}</p>}
              </div>
            ))}
            {options.length < 4 && (
              <button
                className="flex aspect-square flex-col items-center justify-center gap-1 bg-voto-surface text-sm font-medium text-voto-muted transition hover:bg-voto-raised hover:text-white"
                onClick={addOption}
                type="button"
              >
                <span className="text-2xl leading-none" aria-hidden="true">+</span>
                Add option
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-voto-muted">Every option needs a photo and a label.</p>
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium text-voto-fg">Who can vote</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Who can vote">
            {[
              ['public', 'Anyone on Voto can vote', 'In the feed and the Voting Booth, plus your link.'],
              ['link', 'Only people with the link', 'Never in the feed, booth, search or your profile.'],
            ].map(([value, label, hint]) => (
              <label
                key={value}
                className={`flex cursor-pointer items-start gap-3 rounded-xl p-3 ring-1 transition focus-within:ring-2 focus-within:ring-voto-coral ${
                  visibility === value ? 'bg-voto-raised ring-voto-coral' : 'bg-voto-surface ring-voto-line hover:bg-voto-raised'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value={value}
                  checked={visibility === value}
                  onChange={() => setVisibility(value)}
                  className="mt-0.5 accent-voto-coral"
                />
                <span>
                  <span className="block text-sm font-semibold text-voto-fg">{label}</span>
                  <span className="block text-xs text-voto-muted">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <button
          className="w-full rounded-[14px] bg-voto-coral px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-voto-coralDeep disabled:cursor-not-allowed disabled:opacity-60"
          disabled={!isValid || submitting}
          type="submit"
        >
          {submitting ? 'Creating poll…' : 'Create poll'}
        </button>
      </form>
    </main>
  );
}

const SEARCH_LIMIT = 25;

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [draft, setDraft] = useState(q);
  useEffect(() => { setDraft(q); }, [q]);
  const [tab, setTab] = useState('polls');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setHasMore(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setResults([]);
    setOffset(0);
    setHasMore(false);
    fetchJson(`/search?q=${encodeURIComponent(q)}&type=${tab}&limit=${SEARCH_LIMIT}&offset=0`, {
      signal: controller.signal,
    })
      .then((data) => {
        setResults(Array.isArray(data.items) ? data.items : []);
        setOffset(Array.isArray(data.items) ? data.items.length : 0);
        setHasMore(data.has_more || false);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setResults([]);
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [q, tab]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const data = await fetchJson(
        `/search?q=${encodeURIComponent(q)}&type=${tab}&limit=${SEARCH_LIMIT}&offset=${offset}`,
      );
      const rows = Array.isArray(data.items) ? data.items : [];
      setResults((current) => [...current, ...rows]);
      setOffset((current) => current + rows.length);
      setHasMore(data.has_more || false);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-xl px-4 py-6">
      <form
        role="search"
        className="mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          const next = draft.trim();
          if (next) setSearchParams({ q: next });
        }}
      >
        <input
          aria-label="Search polls or people"
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search polls or people…"
          className="w-full rounded-xl border border-voto-line bg-voto-surface px-4 py-2.5 text-sm text-voto-fg outline-none transition focus:border-voto-coral"
        />
      </form>
      <h1 className="text-xl font-semibold text-white">
        {q ? `Results for "${q}"` : 'Search'}
      </h1>

      <nav aria-label="Search results" className="mt-3 flex gap-4 border-b border-voto-line">
        {[['polls', 'Polls'], ['users', 'People']].map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-current={tab === value ? 'page' : undefined}
            onClick={() => setTab(value)}
            className={`-mb-px border-b-2 px-1 pb-2 text-[13px] font-medium transition ${
              tab === value ? 'border-voto-coral text-white' : 'border-transparent text-voto-muted hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {!q.trim() && (
        <p className="mt-8 text-voto-muted">Enter a search term above.</p>
      )}

      {loading && <p className="mt-8 text-voto-muted">Searching…</p>}

      {!loading && q.trim() && results.length === 0 && (
        <p className="mt-8 rounded-xl bg-voto-surface p-4 text-voto-muted">
          No results found.
        </p>
      )}

      {!loading && results.length > 0 && tab === 'polls' && (
        <div className="-mx-4 mt-6 flex flex-col gap-1.5">
          {results.map((poll) => (
            <PollCard key={poll.id} poll={poll} to={`/poll/${poll.id}`} />
          ))}
        </div>
      )}

      {!loading && results.length > 0 && tab === 'users' && (
        <ul className="mt-6 space-y-3">
          {results.map((user) => (
            <li key={user.id}>
              <Link
                className="flex items-center gap-3 rounded-xl bg-voto-surface p-3 ring-1 ring-voto-line transition hover:bg-voto-raised"
                to={`/u/${user.handle}`}
              >
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <Avatar name={user.name || user.handle} handle={user.handle} size="h-10 w-10 text-sm" />
                )}
                <div className="min-w-0 flex-1">
                  {user.name && (
                    <p className="font-medium text-voto-fg">{user.name}</p>
                  )}
                  <p className="text-sm text-voto-muted">@{user.handle}</p>
                </div>
                <span className="flex-shrink-0 text-sm text-voto-muted">
                  {(user.follower_count || 0).toLocaleString()} followers
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="mt-8 flex justify-center">
          <button
            className="rounded-[12px] bg-voto-surface px-5 py-2 text-sm font-semibold text-voto-fg ring-1 ring-voto-line transition hover:bg-voto-raised disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loadingMore}
            type="button"
            onClick={loadMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </main>
  );
}

function AppRoutes() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  // First-party beacon (owned demand telemetry) + GA4, on each route change.
  useEffect(() => {
    trackPageview();
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      {/* The shared poll page stands alone: phone-first, no app chrome, no sign-in. */}
      <Route path="/p/:slug" element={<SharedPollPage />} />
      <Route
        path="*"
        element={
          <Layout user={user} loading={loading} onLogout={logout}>
            <Routes>
              <Route path="feed" element={<FeedPage />} />
              <Route path="booth" element={<BoothPage />} />
              <Route path="credits" element={<CreditsPage />} />
              <Route path="tags" element={<Tags />} />
              <Route path="auth" element={<AuthPage />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="poll/:id" element={<PollDetailPage />} />
              <Route path="polls/new" element={<CreatePollPage />} />
              <Route path="search" element={<SearchPage />} />
              <Route path="u/:handle" element={<ProfilePage />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="terms" element={<Terms />} />
            </Routes>
          </Layout>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
